import React, { useEffect, useState } from 'react';
import { Copy, Crown, ExternalLink, Loader2, Mail, MessageCircle, Trash2, Users } from 'lucide-react';
import { api } from '../../services/api';

const STATUS: Record<string, { label: string; cls: string }> = {
    new: { label: 'Nuevo', cls: 'bg-orange-100 text-orange-600' },
    contacted: { label: 'Contactado', cls: 'bg-sky-100 text-sky-600' },
    assigned: { label: 'Cachorro asignado', cls: 'bg-emerald-100 text-emerald-600' },
    discarded: { label: 'Descartado', cls: 'bg-slate-100 text-slate-400' },
};
const PREF: Record<string, string> = { macho: 'Prefiere macho', hembra: 'Prefiere hembra', indistinto: 'Macho o hembra' };

interface Props { asUser?: string | number; isElite: boolean; slug?: string; onUpgrade: (reason: string) => void; }

export const BreederWaitlist: React.FC<Props> = ({ asUser, isElite, slug, onUpgrade }) => {
    const [entries, setEntries] = useState<any[]>([]);
    const [loading, setLoading] = useState(isElite);
    const [error, setError] = useState('');
    const [filter, setFilter] = useState<'all' | 'new' | 'contacted' | 'assigned' | 'discarded'>('all');

    const load = async () => {
        setLoading(true); setError('');
        try { setEntries(await api.breeder.getWaitlist(asUser)); }
        catch (e: any) { if (e.code === 'PLAN_ELITE') onUpgrade(e.message); else setError(e.message); }
        finally { setLoading(false); }
    };
    useEffect(() => { if (isElite) load(); }, [isElite, asUser]);

    const save = async (e: any, patch: Partial<{ status: string; internal_notes: string }>) => {
        const next = { ...e, ...patch };
        setEntries(list => list.map(x => x.id === e.id ? next : x));
        try { await api.breeder.updateWaitlist({ id: e.id, status: next.status, internalNotes: next.internal_notes || '' }, asUser); }
        catch (err: any) { setError(err.message); load(); }
    };
    const remove = async (e: any) => {
        if (!confirm(`¿Eliminar a ${e.name} de la lista?`)) return;
        try { await api.breeder.deleteWaitlist(e.id, asUser); setEntries(l => l.filter(x => x.id !== e.id)); } catch (err: any) { setError(err.message); }
    };

    const link = slug ? `https://amigopeludo.cl/criadero/${slug}#lista-de-espera` : '';

    if (!isElite) return (
        <div className="bg-white rounded-[2.5rem] p-10 shadow-xl border border-slate-100 text-center max-w-2xl mx-auto">
            <span className="w-16 h-16 bg-amber-400 rounded-[7px] flex items-center justify-center mx-auto mb-5 shadow-lg"><Crown size={28} className="text-white" /></span>
            <h3 className="text-2xl font-black text-slate-800 mb-2">Lista de espera de compradores</h3>
            <p className="text-slate-500 font-medium leading-relaxed mb-6">Tu página pública recibe un formulario donde los interesados se anotan para tu próxima camada. Tú ves quién es, lo contactas y le asignas un cachorro. Disponible en el plan <strong>Criador Élite</strong>.</p>
            <button onClick={() => onUpgrade('La lista de espera de compradores es parte del plan Criador Élite.')} className="px-8 py-4 bg-slate-900 hover:bg-orange-500 text-white rounded-[7px] font-black uppercase text-xs tracking-widest transition-colors">Ver plan Élite</button>
        </div>
    );

    const shown = entries.filter(e => filter === 'all' || e.status === filter);

    return (
        <div className="space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
                <div>
                    <h2 className="text-2xl font-black text-slate-800 uppercase italic tracking-tighter flex items-center gap-2"><Users className="text-orange-500" size={24} /> Lista de espera</h2>
                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Interesados que se anotaron en tu página pública ({entries.length})</p>
                </div>
                {link && (
                    <button onClick={() => navigator.clipboard?.writeText(link).catch(() => { })} className="inline-flex items-center gap-2 px-5 py-3 bg-orange-50 hover:bg-orange-100 text-orange-600 rounded-[7px] text-[10px] font-black uppercase tracking-widest transition-colors self-start">
                        <Copy size={14} /> Copiar enlace del formulario
                    </button>
                )}
            </div>

            <div className="flex flex-wrap gap-2">
                {(['all', 'new', 'contacted', 'assigned', 'discarded'] as const).map(k => (
                    <button key={k} onClick={() => setFilter(k)} className={`px-4 py-2 rounded-full text-[10px] font-black uppercase tracking-widest transition-all ${filter === k ? 'bg-[#FF6B00] text-white shadow' : 'bg-white border border-slate-200 text-slate-500 hover:border-orange-300'}`}>
                        {k === 'all' ? 'Todos' : STATUS[k].label} <span className={filter === k ? 'text-orange-200' : 'text-slate-300'}>{k === 'all' ? entries.length : entries.filter(e => e.status === k).length}</span>
                    </button>
                ))}
            </div>

            {error && <p className="text-xs font-bold text-rose-500 bg-rose-50 rounded-[7px] px-4 py-3">{error}</p>}
            {loading ? <div className="py-16 text-center"><Loader2 className="animate-spin mx-auto text-orange-500" /></div>
                : shown.length === 0 ? (
                    <div className="py-14 text-center bg-slate-50 rounded-[2.5rem] border-2 border-dashed border-slate-200">
                        <Users size={36} className="mx-auto text-slate-300 mb-3" />
                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{entries.length ? 'No hay solicitudes con ese estado' : 'Aún nadie se anota'}</p>
                        {!entries.length && link && <p className="text-[11px] text-slate-400 font-medium mt-2">Comparte el enlace del formulario en tus redes o por WhatsApp.</p>}
                    </div>
                ) : (
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        {shown.map(e => {
                            const phone = String(e.phone || '').replace(/[^0-9]/g, '');
                            return (
                                <div key={e.id} className="bg-white rounded-[2rem] p-5 border border-slate-100 shadow-sm">
                                    <div className="flex items-start justify-between gap-3">
                                        <div className="min-w-0">
                                            <p className="text-base font-black text-slate-800 truncate">{e.name}</p>
                                            <p className="text-[11px] font-bold text-slate-400 truncate">{e.email}{e.city ? ` · ${e.city}` : ''}</p>
                                        </div>
                                        <span className={`px-3 py-1 rounded-full text-[8px] font-black uppercase tracking-widest shrink-0 ${STATUS[e.status]?.cls || STATUS.new.cls}`}>{STATUS[e.status]?.label || e.status}</span>
                                    </div>
                                    <div className="flex flex-wrap gap-2 mt-3">
                                        <span className="px-3 py-1 bg-slate-100 text-slate-500 rounded-full text-[9px] font-black uppercase">{PREF[e.preference] || PREF.indistinto}</span>
                                        <span className="px-3 py-1 bg-slate-50 text-slate-400 rounded-full text-[9px] font-black uppercase">{new Date(String(e.created_at).replace(' ', 'T')).toLocaleDateString('es-CL')}</span>
                                    </div>
                                    {e.notes && <p className="mt-3 text-[12px] text-slate-600 font-medium italic bg-slate-50 rounded-[7px] px-3 py-2">"{e.notes}"</p>}
                                    <textarea rows={2} defaultValue={e.internal_notes || ''} placeholder="Notas internas (solo tú las ves)"
                                        onBlur={ev => { if (ev.target.value !== (e.internal_notes || '')) save(e, { internal_notes: ev.target.value }); }}
                                        className="w-full mt-3 bg-slate-50 border border-slate-100 rounded-[7px] px-3 py-2 text-[12px] font-medium resize-none focus:outline-none focus:border-orange-300" />
                                    <div className="flex flex-wrap items-center gap-2 mt-3">
                                        <select value={e.status} onChange={ev => save(e, { status: ev.target.value })} className="bg-white border border-slate-200 rounded-[7px] px-3 py-2 text-[11px] font-black text-slate-600">
                                            {Object.entries(STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                                        </select>
                                        <a href={`mailto:${e.email}`} className="p-2.5 bg-slate-100 hover:bg-slate-200 text-slate-500 rounded-[7px]" title="Escribir"><Mail size={14} /></a>
                                        {phone && <a href={`https://wa.me/${phone}`} target="_blank" rel="noreferrer" className="p-2.5 bg-emerald-50 hover:bg-emerald-100 text-emerald-600 rounded-[7px]" title="WhatsApp"><MessageCircle size={14} /></a>}
                                        <button onClick={() => remove(e)} className="p-2.5 bg-rose-50 hover:bg-rose-100 text-rose-400 rounded-[7px] ml-auto" title="Eliminar"><Trash2 size={14} /></button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
        </div>
    );
};

export default BreederWaitlist;
